import React from "react";
import {
	SEGMENT_BZA,
	URL_BZA_AIRCRAFT_DATA,
	URL_BZA_AIRCRAFT_HISTORY_DATA,
	URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
	URL_BZA_OPERATOR_AIRPORT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productSegmentPageProps } from "../../common/types/product";

export default function BusinessJetAircraftAccidentsIncidentsData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="The Accidents and Incidents data offers a comprehensive overview of accidents and incidents by business jet operator and/or aircraft variant."
				packages={[
					{
						label: SEGMENT_BZA,
						features: [
							"Global coverage of aircraft accidents and incidents on a tail, operator, and airport level since the late 1950’s",
							"Accident and Incident information data is provided by the Flight Safety Foundation and mapped with ch-aviation fleet data",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Aircraft Accidents and Incidents",
						description:
							"Business Jet Accidents and Incidents Data is available on a tail, operator and airport level; and includes info such as:",
						list: [
							"Aircraft accident or incident event overview (including date and time of the event, aircraft manufacturer, variant, and registration)",
							"Operator insights (name of the operator, country, and type)",
							"Aircraft insights (the exact model of aircraft, age, C/N, MSN, production line)",
							"Engine details (which engines are powering the aircraft, including engine type and subtype)",
							"Aircraft status (whether the aircraft was written off after the event)",
							"Casualties (including total casualties and total occupants on the plane)",
							"Flight profile (including flight nature, flight phase, and event coordinates)",
							"Event location (airport and country where the event happened)",
							"Origin and destination (airport name and country for both origin and destination)",
							"Maps (accident and incident event location displayed on the map)",
							"Event profile (including damage type, occurrence category, loss category, and subcategory)",
							"Remarks and source (detailed explanation of the accident and incident event and the source)",
						],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-profile-overview.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search business jet accidents and incidents by operator or aircraft variant, casualties, flight phase, location, damage, loss category/subcategory, and more."
				slides={[
					{
						title: "Accident/Incident",
						description:
							"Search for business jet accident and incident based on:",
						list: [
							"Write Off (if the aircraft was written off after an accident or incident)",
							"Casualties",
							"Occupants",
							"Flight nature (passenger, cargo, military, etc.)",
							"Damage (minor, substantial, none, etc.)",
							"Flight phase (landing, takeoff, climb, etc.)",
							"Occurrence Category (accident, incident, etc.)",
							"Loss category (act of nature, accidental act, etc.)",
							"Loss subcategory (turbulence, bird strike, etc.)",
						],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-search-by-category.jpg",
							title: "List of all minor business jet incidents caused by acts of nature",
						},
					},
					{
						title: "Location",
						description:
							"Search for business jet accident and incident by location:",
						list: [
							"Operator location (Continent, Country, Region)",
							"Accident/incident event location (continent, country, airport location)",
							"Flight origin (Continent, Country, Airport Location)",
							"Flight destination (Continent, Country, Airport Location)",
						],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-search-by-location.jpg",
							title: "List of all European-registered business jets involved in accidents in the United States of America",
						},
					},
					{
						title: "Operator",
						description:
							"Search for aircraft accidents and incidents by:",
						list: ["Operator name", "Operator type"],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-search-by-operator.jpg",
							title: "Overview of all NetJets Aviation accidents and incidents",
						},
					},
					{
						title: "Aircraft Type",
						description:
							"Search for business jet accidents and incidents by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft production line",
							"Aircraft family",
							"Aircraft variant",
						],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-search-by-aircraft-type.jpg",
							title: "All business jet accidents and incidents involving Gulfstream GV",
						},
					},
					{
						title: "Aircraft",
						description:
							"Search for business jet accidents and incidents by:",
						list: [
							"Aircraft registration",
							"Aircraft line/serial number",
							"Aircraft block number",
							"Aircraft age",
						],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-search-by-aircraft.jpg",
							title: "All accidents and incidents involving aircraft registered as N424QS",
						},
					},
					{
						title: "Engine",
						description:
							"Search for business jet accidents and incidents by:",
						list: [
							"Engine manufacturer",
							"Engine family",
							"Engine master series",
							"Engine type/subtype",
						],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-search-by-engine.jpg",
							title: "List of all accidents and incidents involving business jets powered by Williams International engines",
						},
					},
					{
						title: "Dates",
						description:
							"Search for business jet aircraft accidents and incidents by:",
						list: [
							"Accident date",
							"Date built (search for aircraft built within a selected date range)",
							"Delivery date (search for aircraft delivered within a selected date range)",
						],
						visual: {
							url: "/static/img/product/business-jet-accidents-and-incidents-data/accidents-and-incidents-search-by-dates.jpg",
							title: "All accidents and incidents in 2024, involving business jets delivered in 2015",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Get access to more than 52,000 business jet operators and over 14,000 airport profiles",
						url: URL_BZA_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Business Jet Aircraft Data",
						content:
							"Get access to more than 29,000 business jets worldwide, with detailed insights at the tail level.",
						url: URL_BZA_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into business jet aircraft owners, managers, and SPVs.",
						url: URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Aircraft History Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_BZA_AIRCRAFT_HISTORY_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Global coverage of business jets accidents and incidents on a tail, operator, and airport level since the late 1950s",
					"Loss Categories and subcategories incorporated for all events, as of 2013",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Business Jet Aircraft Accidents and Incidents Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"Business Jet Accidents and Incidents Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Business Jet Aircraft Accident and Incident
								Data can be purchased as a data feed for
								seamless integration into your own system.
								ch-aviation also provides full support for the
								integration process. To learn more about data
								feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "What's the data source?",
						content:
							"This module launched in cooperation with the Flight Safety Foundation. It features a comprehensive overview of accidents and incidents on an aircraft tail number level since the late 1950s. In addition, we have also incorporated detailed loss categories and subcategories for all events as of 2013, allowing you to get a deeper understanding of each incident or accident.",
					},
					{
						title: "What are the main loss categories ch-aviation is tracking?",
						content: (
							<ul className="ticks ticks--small">
								<li>Accident</li>
								<li>Incident</li>
								<li>Serious incident</li>
								<li>Unknown occurrence</li>
								<li>Unlawful interference</li>
							</ul>
						),
					},
				]}
			/>
		</>
	);
}
